import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CircleNotch,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import {
  notifications as notificationsApi,
  prospects as prospectsApi,
  stats,
} from "@/api";
import FinancialPeriodDashboard, {
  createDefaultFinancialPeriod,
  financialPeriodRequest,
} from "@/components/dashboard/FinancialPeriodDashboard";
import DashboardNotificationButton from "@/components/dashboard/DashboardNotificationButton";
import DashboardSkeleton from "@/components/dashboard/DashboardSkeleton";
import MobileCommercialHomeV2 from "@/components/dashboard/MobileCommercialHomeV2";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";


function safeArray(value) {
  return Array.isArray(value)
    ? value
    : [];
}


function numberValue(value) {
  const parsed =
    Number(value || 0);

  return Number.isFinite(parsed)
    ? parsed
    : 0;
}


function localDateKey(value) {
  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  const year =
    date.getFullYear();

  const month =
    String(
      date.getMonth() + 1
    ).padStart(2, "0");

  const day =
    String(
      date.getDate()
    ).padStart(2, "0");

  return `${year}-${month}-${day}`;
}


function countTodayMeetings(rows) {
  const today =
    localDateKey(
      new Date()
    );

  return safeArray(rows)
    .filter((item) => (
      item?.nextActionType === "meeting"
      && localDateKey(
        item?.nextActionAt
      ) === today
      && (
        !item?.nextActionStatus
        || item.nextActionStatus === "open"
      )
    ))
    .length;
}


function dashboardErrorMessage(error) {
  const detail = (
    error?.response?.data?.detail
    ?? error?.message
    ?? "Le tableau de bord est momentanément indisponible."
  );

  return (
    formatApiError(detail)
    || "Le tableau de bord est momentanément indisponible."
  );
}


function filterCommercialUsers(
  rows,
  currentUserId
) {
  return safeArray(rows)
    .filter((entry) => {
      if (!entry?.id) {
        return false;
      }

      if (
        entry.id
        === currentUserId
      ) {
        return false;
      }

      return [
        "admin",
        "manager",
        "user",
      ].includes(
        entry.role
      );
    });
}


function DashboardError({
  message,
  onRetry,
}) {
  return (
    <main
      className="
        mx-auto max-w-[1600px]
        px-4 py-8
        sm:px-6
        lg:p-8
      "
    >
      <section
        className="
          au-card
          p-8 text-center
        "
      >
        <CircleNotch
          size={38}
          weight="duotone"
          className="mx-auto"
          style={{
            color:
              "var(--do-accent)",
          }}
        />

        <h1
          className="
            mt-4 font-display
            text-2xl
          "
          style={{
            color:
              "var(--do-ink)",
          }}
        >
          Vue d’ensemble
        </h1>

        <p
          className="mt-2 text-sm"
          style={{
            color:
              "var(--do-muted)",
          }}
        >
          {message}
        </p>

        <button
          type="button"
          onClick={onRetry}
          className="
            mt-5 rounded-xl
            bg-[var(--do-accent-strong)]
            px-5 py-2.5
            text-sm font-semibold
            text-[var(--do-on-accent)]
          "
        >
          Réessayer
        </button>
      </section>
    </main>
  );
}


export default function Dashboard() {
  const { user } =
    useAuth();

  const [dashboard, setDashboard] =
    useState(null);

  const [
    notificationData,
    setNotificationData,
  ] = useState({
    notifications: [],
    unreadCount: 0,
  });

  const [period, setPeriod] =
    useState(
      () =>
        createDefaultFinancialPeriod()
    );

  const [
    ownerUserId,
    setOwnerUserId,
  ] = useState("");

  const [
    commercialUsers,
    setCommercialUsers,
  ] = useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [
    notificationsOpen,
    setNotificationsOpen,
  ] = useState(false);

  const [
    agendaRows,
    setAgendaRows,
  ] = useState([]);

  const requestIdRef =
    useRef(0);

  const canFilterTeam =
    user?.role === "admin"
    || user?.role === "manager";

  const todayMeetings =
    useMemo(
      () =>
        countTodayMeetings(
          agendaRows
        ),
      [agendaRows]
    );

  useEffect(() => {
    if (!canFilterTeam) {
      setCommercialUsers([]);
      setOwnerUserId("");
      return undefined;
    }

    let cancelled = false;

    prospectsApi
      .listCommercialUsers()
      .then((rows) => {
        if (cancelled) {
          return;
        }

        setCommercialUsers(
          filterCommercialUsers(
            rows,
            user?.id
          )
        );
      })
      .catch(() => {
        if (!cancelled) {
          setCommercialUsers([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    canFilterTeam,
    user?.id,
  ]);

  const loadDashboard =
    useCallback(
      async () => {
        const requestId =
          requestIdRef.current
          + 1;

        requestIdRef.current =
          requestId;

        setLoading(true);
        setError("");

        const [
          dashboardResult,
          notificationsResult,
          agendaResult,
        ] = await Promise.allSettled([
          stats.dashboardV2({
            ...financialPeriodRequest(
              period
            ),
            ownerUserId,
          }),

          notificationsApi.list(
            false
          ),

          prospectsApi.agenda(),
        ]);

        if (
          requestId
          !== requestIdRef.current
        ) {
          return;
        }

        if (
          dashboardResult.status
          === "fulfilled"
        ) {
          setDashboard(
            dashboardResult.value
          );
        } else {
          setError(
            dashboardErrorMessage(
              dashboardResult.reason
            )
          );
        }

        if (
          notificationsResult.status
          === "fulfilled"
        ) {
          const response =
            notificationsResult.value;

          setNotificationData({
            notifications:
              safeArray(
                response?.notifications
              ),
            unreadCount:
              numberValue(
                response?.unreadCount
              ),
          });
        } else {
          setNotificationData({
            notifications: [],
            unreadCount: 0,
          });
        }

        if (
          agendaResult.status
          === "fulfilled"
        ) {
          setAgendaRows(
            safeArray(
              agendaResult.value
            )
          );
        } else {
          setAgendaRows([]);
        }

        setLoading(false);
      },
      [
        ownerUserId,
        period,
      ]
    );

  useEffect(() => {
    loadDashboard();
  }, [
    loadDashboard,
  ]);

  const markAllNotificationsRead =
    useCallback(
      async () => {
        try {
          await notificationsApi
            .markAllRead();

          setNotificationData(
            (current) => ({
              notifications:
                current.notifications
                  .map(
                    (notification) => ({
                      ...notification,
                      read: true,
                      readAt:
                        notification.readAt
                        || new Date()
                          .toISOString(),
                    })
                  ),
              unreadCount: 0,
            })
          );

          toast.success(
            "Toutes les notifications ont été marquées comme lues."
          );
        } catch (requestError) {
          toast.error(
            dashboardErrorMessage(
              requestError
            )
          );
        }
      },
      []
    );

  const closeNotifications =
    useCallback(
      () => {
        setNotificationsOpen(
          false
        );
      },
      []
    );

  const toggleNotifications =
    useCallback(
      () => {
        setNotificationsOpen(
          (current) =>
            !current
        );
      },
      []
    );

  if (
    loading
    && !dashboard
  ) {
    return (
      <DashboardSkeleton />
    );
  }

  if (
    error
    && !dashboard
  ) {
    return (
      <DashboardError
        message={error}
        onRetry={loadDashboard}
      />
    );
  }

  return (
    <main
      className="
        relative min-h-full
      "
      data-testid="dashboard-page"
    >
      <div className="lg:hidden">
        <MobileCommercialHomeV2
          dashboard={dashboard}
          period={period}
          onPeriodChange={
            setPeriod
          }
          canFilterTeam={
            canFilterTeam
          }
          ownerUserId={
            ownerUserId
          }
          onOwnerUserIdChange={
            setOwnerUserId
          }
          commercialUsers={
            commercialUsers
          }
          currentUser={user}
          agendaRows={
            agendaRows
          }
        />
      </div>

      <div className="hidden lg:block">
        <FinancialPeriodDashboard
          dashboard={dashboard}
          period={period}
          onPeriodChange={
            setPeriod
          }
          canFilterTeam={
            canFilterTeam
          }
          ownerUserId={
            ownerUserId
          }
          onOwnerUserIdChange={
            setOwnerUserId
          }
          commercialUsers={
            commercialUsers
          }
          currentUser={user}
          todayMeetings={
            todayMeetings
          }
          refreshing={
            loading
          }
          notificationButton={
            <DashboardNotificationButton
              notifications={
                notificationData.notifications
              }
              unreadCount={
                notificationData.unreadCount
              }
              open={
                notificationsOpen
              }
              onToggle={
                toggleNotifications
              }
              onClose={
                closeNotifications
              }
              onMarkAllRead={
                markAllNotificationsRead
              }
            />
          }
        />
      </div>
    </main>
  );
}
